import { Box, Button, Heading, HStack, IconButton, Skeleton, Tooltip, VStack } from "@chakra-ui/react";
import { FunctionComponent, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { HiChevronLeft } from "react-icons/hi";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as PlusIcon } from "../../../assets/svg/PlusIcon.svg";

// Components
import LayoutWrapper from "../../../shared/layout";
import { Page } from "../../../shared/layout/index.styles";
import SectionHeader from "../../../shared/SectionHeader/SectionHeader";
import DataCardDisplay from "../../../shared/DataDisplay/DataDisplay";
import LoadingSpinner from "../../../shared/LoadingSpinner/LoadingSpinner";
import ReservationsTable from "./ReservationsTable";

// Hooks
import useClaimsToast from "../../../hooks/useToast";
import usePermission from "../../../hooks/usePermission";

// Utils
import { getUserDataById } from "../../../utils/api/auth";
import { convertUnixTimestampToLocalDate } from "../../../utils/helpers";
import { reservationManagementRoles, UserRole } from "../../../utils/consts";

// Types
import { KVP } from "../../../utils/types";
import EditUserModal from "./modals/EditUserModal";

interface UserDetailProps {}

interface LocationState {
	userData?: KVP;
}

const UserDetail: FunctionComponent<UserDetailProps> = (): ReactElement => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as LocationState;
	const toast = useClaimsToast();
	const { roles } = usePermission();

	const [user, setUser] = useState<KVP | undefined>(locationState?.userData);
	const [isLoading, setIsLoading] = useState<boolean>(!locationState?.userData);
	const [isEditUserModalOpen, setIsEditUserModalOpen] = useState<boolean>(false);
	const [triggerAddReservation, setTriggerAddReservation] = useState<boolean>(false);

	const userRoles = useMemo(() => {
		// Fetch policy and benefits if possible
		return ((user?.roles as string)?.split(", ") as UserRole[]) ?? [];
	}, [user]);

	// Check if user has management access
	const hasManagementAccess = reservationManagementRoles.some((role) => roles.includes(role as UserRole));
	const hasEditAccess = roles.includes(UserRole.ClaimsNetworkManager);

	const fetchUser = useCallback(
		async (userId: number) => {
			try {
				const response = await getUserDataById(userId);
				const userData = response.getUser();

				if (!userData) {
					toast("Error", "User not found", "error");
					navigate("/users");
					return;
				}

				// Map UserData proto to KVP format (full data with status/timestamps)
				const mappedUser: KVP = {
					id: userData.getId(),
					email: userData.getEmail(),
					username: userData.getUsername(),
					name: userData.getName(),
					roles: userData.getRole(),
					status: userData.getStatus(),
					createdAt: userData.getCreatedAt()
						? convertUnixTimestampToLocalDate(userData.getCreatedAt()).toLocaleString()
						: "N/A",
					confirmedAt: userData.getConfirmedAt()
						? convertUnixTimestampToLocalDate(userData.getConfirmedAt()).toLocaleString()
						: "Not Confirmed"
				};
				setUser(mappedUser);
			} catch (err) {
				console.error("Error fetching user:", err);
				toast("Error", "Unable to load user details", "error");
				navigate("/users");
			} finally {
				setIsLoading(false);
			}
		},
		[navigate, toast]
	);

	useEffect(() => {
		// If we don't have user data from router state or local state, fetch it
		if (!locationState?.userData && !user && id) {
			const userId = parseInt(id);
			if (!isNaN(userId)) {
				fetchUser(userId);
			} else {
				toast("Error", "Invalid user ID", "error");
				navigate("/users");
			}
		}
	}, [id, locationState?.userData, user, fetchUser, toast, navigate]);

	return (
		<LayoutWrapper>
			{isLoading && <LoadingSpinner />}
			{isEditUserModalOpen && (
				<EditUserModal
					user={user}
					isOpen={isEditUserModalOpen}
					onClose={() => setIsEditUserModalOpen(false)}
					onSubmit={(user) => {
						setIsEditUserModalOpen(false);
						setUser(user);
					}}
				/>
			)}
			<HStack height="100%" overflow="hidden" gap="0">
				<Box id="pageBody" width="100%" height="100%" overflow="auto">
					<Box mx={8}>
						<Page overflow="initial">
							{/* Header Section */}
							<HStack
								alignItems="center"
								margin="0 auto"
								position="sticky"
								top="0px"
								zIndex="2"
								background="white"
								marginTop="-15px"
								paddingTop="15px">
								<Box width="200px">
									<Button leftIcon={<HiChevronLeft />} variant="ghost" onClick={() => navigate("/users")}>
										Back
									</Button>
								</Box>
								<Heading fontSize="21px" fontWeight="700" color="gray.700" flex={1} textAlign="center">
									{user?.name || "User"}
								</Heading>
								<Box width="200px" display="flex" flexDirection="column" alignItems="flex-end" />
							</HStack>

							{/* Details Section */}
							<VStack spacing={5} align="stretch" mt={4}>
								<Box>
									<SectionHeader
										title="Details"
										width="100%"
										showEditButton={hasEditAccess && userRoles.includes(UserRole.Provider)}
										onEditButtonClick={() => setIsEditUserModalOpen(true)}
									/>
									<Skeleton isLoaded={!!user} endColor="gray.200">
										<DataCardDisplay
											data={[
												{
													title: "User Id",
													value: user?.id?.toString() || "N/A"
												},
												{
													title: "External Id",
													value: user?.externalId?.toString() || "N/A"
												},
												{
													title: "Username",
													value: user?.username?.toString() || "N/A"
												},
												{
													title: "Email",
													value: user?.email?.toString() || "N/A"
												},
												{
													title: "Roles",
													value: user?.roles?.toString() || "N/A"
												},
												...(user?.status !== undefined && user?.status !== null
													? [
															{
																title: "Status",
																value: String(user.status)
															}
														]
													: []),
												...(user?.createdAt
													? [
															{
																title: "Created At",
																value: user.createdAt.toString()
															}
														]
													: []),
												...(user?.confirmedAt
													? [
															{
																title: "Confirmed At",
																value: user.confirmedAt.toString()
															}
														]
													: [])
											]}
											leftSideRows={4}
											style={{ width: "100%" }}
											leftLabelMinWidth={90}
											rightLabelMinWidth={110}
										/>
									</Skeleton>
								</Box>
							</VStack>

							{/* Claim Reservations Section - Only show for Claims Analysts and Managers */}
							{user && (userRoles.includes(UserRole.ClaimsAnalyst) || userRoles.includes(UserRole.ClaimsManager)) && (
								<VStack spacing={5} align="stretch" mt={8}>
									<Box>
										<SectionHeader
											title="Claim Reservations"
											width="100%"
											customAction={
												hasManagementAccess ? (
													<Tooltip label="Add Reservation" placement="left">
														<IconButton
															aria-label="Add reservation"
															icon={<PlusIcon style={{ width: "18px", height: "17px", fill: "#FFF" }} />}
															size="sm"
															variant="ghost"
															colorScheme="whiteAlpha"
															color="white"
															_hover={{ color: "gray.200" }}
															onClick={() => setTriggerAddReservation(true)}
														/>
													</Tooltip>
												) : undefined
											}
										/>
										<Box mt={4}>
											<ReservationsTable
												userId={Number(user.id)}
												userName={String(user.name)}
												triggerAddModal={triggerAddReservation}
												onModalClose={() => setTriggerAddReservation(false)}
											/>
										</Box>
									</Box>
								</VStack>
							)}
						</Page>
					</Box>
				</Box>
			</HStack>
		</LayoutWrapper>
	);
};

export default UserDetail;
